import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Loader2, CheckCircle2, X } from "lucide-react";
import { Reveal, SectionTag } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-2 border-zinc-300 bg-white px-4 py-3 text-zinc-950 placeholder-zinc-400 focus:border-red-700 focus:outline-none transition-colors text-sm font-medium";

export const BulkScrap = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", location: "", material_type: "", quantity: "", description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - photos.length);
    setPhotos([...photos, ...files]);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/bulk-quote`, fd);
      setDone(true);
      toast.success("Quote request sent. We'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please call us at 740-747-1123.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="bulk-scrap" className="bg-white py-20 sm:py-28" data-testid="bulk-scrap-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionTag number="04" label="Bulk Scrap" />
              <h2 className="font-heading text-4xl sm:text-5xl text-zinc-950 tracking-tight leading-[1.05]">
                GOT A SERIOUS LOAD? <span className="text-red-700">LET'S TALK.</span>
              </h2>
              <p className="mt-6 text-lg text-zinc-700 font-medium">
                Scrapman's purchases qualifying bulk loads from businesses, contractors, farms, shops and other large-volume sellers.
              </p>
              <p className="mt-4 text-zinc-600">
                Pickup may be available for qualifying loads depending on the material, quantity, location and value.
              </p>
              <div className="mt-8 border-l-4 border-red-700 bg-zinc-50 p-5">
                <p className="text-sm font-semibold text-zinc-800">
                  Final pricing is based on actual material grade, weight and current market conditions.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {done ? (
                <div className="border-2 border-zinc-950 p-10 sm:p-14 text-center bg-zinc-50" data-testid="bulk-form-success">
                  <CheckCircle2 className="h-14 w-14 text-red-700 mx-auto" />
                  <h3 className="font-heading text-3xl text-zinc-950 mt-6 tracking-wide">REQUEST RECEIVED</h3>
                  <p className="mt-3 text-zinc-600">We'll review your load details and get back to you. Need an answer fast? Call 740-747-1123.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="border-2 border-zinc-950 p-6 sm:p-8 bg-white shadow-[8px_8px_0px_0px_rgba(10,10,10,1)]" data-testid="bulk-quote-form">
                  <p className="font-heading text-xl tracking-wide text-zinc-950 mb-6">REQUEST A BULK SCRAP QUOTE</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="bulk-input-name" />
                    <input required type="tel" placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="bulk-input-phone" />
                    <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="bulk-input-email" />
                    <input required placeholder="Location (city / county) *" value={form.location} onChange={set("location")} className={inputCls} data-testid="bulk-input-location" />
                    <input required placeholder="Material type *" value={form.material_type} onChange={set("material_type")} className={inputCls} data-testid="bulk-input-material" />
                    <input required placeholder="Approximate quantity *" value={form.quantity} onChange={set("quantity")} className={inputCls} data-testid="bulk-input-quantity" />
                  </div>
                  <textarea
                    rows={3}
                    placeholder="Short description of the load"
                    value={form.description}
                    onChange={set("description")}
                    className={`${inputCls} mt-4 resize-none`}
                    data-testid="bulk-input-description"
                  />

                  <label className="mt-4 flex items-center gap-3 border-2 border-dashed border-zinc-300 px-4 py-4 cursor-pointer hover:border-red-700 transition-colors" data-testid="bulk-photo-upload">
                    <Upload className="h-5 w-5 text-zinc-500 shrink-0" />
                    <span className="text-sm font-medium text-zinc-600">
                      {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? "s" : ""} added` : "Add photos of your material (up to 6)"}
                    </span>
                    <input type="file" accept="image/*" multiple onChange={onPhotos} className="hidden" />
                  </label>

                  {photos.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {photos.map((p, i) => (
                        <span key={i} className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-300 px-3 py-1.5 text-xs font-medium text-zinc-700">
                          {p.name.length > 22 ? p.name.slice(0, 20) + "…" : p.name}
                          <button type="button" onClick={() => setPhotos(photos.filter((_, j) => j !== i))} className="text-zinc-500 hover:text-red-700" data-testid={`bulk-photo-remove-${i}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-zinc-950 text-white px-8 py-4 font-heading text-lg tracking-wider hover:bg-red-700 transition-colors disabled:opacity-60"
                    data-testid="bulk-form-submit"
                  >
                    {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                    REQUEST A BULK SCRAP QUOTE
                  </button>
                  <p className="mt-4 text-xs leading-relaxed text-zinc-500" data-testid="bulk-form-consent">
                    By submitting, you agree we may contact you by phone or email about this request. We
                    don't sell your information and we don't send marketing texts. See our{" "}
                    <Link to="/privacy-policy" className="underline underline-offset-2 hover:text-red-700">
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link to="/terms-of-use" className="underline underline-offset-2 hover:text-red-700">
                      Terms of Use
                    </Link>
                    . Estimates are not guaranteed prices — final payment is based on actual grade and
                    weight at our scale.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
